import {
  ChangeEvent,
  FocusEvent,
  InputHTMLAttributes,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { cn } from '../../lib/utils';

export interface DateOfBirthInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> {
  /** ISO date (`yyyy-mm-dd`) or an empty / unset value. */
  value?: string;
  /** Called with the ISO date (`yyyy-mm-dd`), or `''` when cleared. */
  onChange: (value: string) => void;
}

type Segment = 'day' | 'month' | 'year';

interface Segments {
  day: string;
  month: string;
  year: string;
}

const SEGMENT_MAX_LENGTH: Record<Segment, number> = { day: 2, month: 2, year: 4 };
const SEGMENT_PLACEHOLDER: Record<Segment, string> = { day: 'dd', month: 'mm', year: 'yyyy' };

const EMPTY_SEGMENTS: Segments = { day: '', month: '', year: '' };

/** `yyyy-mm-dd…` → its three `dd` / `mm` / `yyyy` parts. */
function splitIso(iso?: string): Segments {
  const match = iso ? /^(\d{4})-(\d{2})-(\d{2})/.exec(iso) : null;
  if (!match) return { ...EMPTY_SEGMENTS };
  return { day: match[3], month: match[2], year: match[1] };
}

/**
 * The three parts → ISO `yyyy-mm-dd`, or null when incomplete/invalid.
 * Single-digit day/month values are zero-padded (`4/3/1990` → `1990-03-04`).
 */
function toIso({ day, month, year }: Segments): string | null {
  const dayPart = day.padStart(2, '0');
  const monthPart = month.padStart(2, '0');
  if (dayPart.length !== 2 || monthPart.length !== 2 || year.length !== 4) return null;
  const d = Number(dayPart);
  const m = Number(monthPart);
  const y = Number(year);
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  const date = new Date(y, m - 1, d);
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) {
    return null;
  }
  return `${year}-${monthPart}-${dayPart}`;
}

function isEmpty(segments: Segments): boolean {
  return !segments.day && !segments.month && !segments.year;
}

/**
 * Date-of-birth field rendered as three `dd` / `mm` / `yyyy` segments. The
 * whole field is a single tab stop, and the left/right arrow keys move between
 * segments (day → month → year, and back), matching the native date picker.
 * Because each part is its own input, any segment can be edited without
 * disturbing the others. The component still reports an ISO `yyyy-mm-dd` value
 * so surrounding forms and the API keep working unchanged.
 */
export function DateOfBirthInput({
  value,
  onChange,
  className,
  disabled,
  ...props
}: DateOfBirthInputProps) {
  const [segments, setSegments] = useState<Segments>(() => splitIso(value));
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dayRef = useRef<HTMLInputElement>(null);
  const monthRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);
  const focusedRef = useRef(false);

  // Re-sync when the value changes elsewhere (e.g. estimated from age) unless
  // the user is currently editing a segment.
  useEffect(() => {
    if (!focusedRef.current) setSegments(splitIso(value));
  }, [value]);

  const emit = (next: Segments) => {
    if (isEmpty(next)) {
      onChange('');
      return;
    }
    const iso = toIso(next);
    if (iso !== null) onChange(iso);
  };

  const changeSegment = (segment: Segment) => (event: ChangeEvent<HTMLInputElement>) => {
    const digits = event.target.value.replace(/\D/g, '').slice(0, SEGMENT_MAX_LENGTH[segment]);
    const next = { ...segments, [segment]: digits };
    setSegments(next);
    emit(next);
  };

  const focusSegment = (segment: Segment) => {
    const el =
      segment === 'day' ? dayRef.current : segment === 'month' ? monthRef.current : yearRef.current;
    if (!el) return;
    el.focus();
    el.select();
  };

  const handleSegmentKeyDown =
    (segment: Segment) => (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'ArrowRight' && segment !== 'year') {
        event.preventDefault();
        focusSegment(segment === 'day' ? 'month' : 'year');
      } else if (event.key === 'ArrowLeft' && segment !== 'day') {
        event.preventDefault();
        focusSegment(segment === 'year' ? 'month' : 'day');
      }
    };

  // Discard a partial/invalid entry and restore the last valid date once focus
  // leaves the whole field; moving between segments keeps it untouched.
  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget;
    if (next instanceof Node && wrapperRef.current?.contains(next)) return;
    focusedRef.current = false;
    if (isEmpty(segments)) return;
    const iso = toIso(segments);
    // Normalise a valid entry (e.g. `4/3/1990` → `04/03/1990`); otherwise revert.
    setSegments(splitIso(iso ?? value));
  };

  const segmentClass = 'bg-transparent border-0 p-0 text-inherit outline-none focus:outline-none';
  const segmentHandlers = (segment: Segment) => ({
    type: 'text' as const,
    inputMode: 'numeric' as const,
    autoComplete: 'off',
    maxLength: SEGMENT_MAX_LENGTH[segment],
    placeholder: SEGMENT_PLACEHOLDER[segment],
    'aria-label': segment[0].toUpperCase() + segment.slice(1),
    disabled,
    className: segmentClass,
    value: segments[segment],
    onFocus: (event: FocusEvent<HTMLInputElement>) => {
      focusedRef.current = true;
      event.currentTarget.select();
    },
    onKeyDown: handleSegmentKeyDown(segment),
    onChange: changeSegment(segment),
  });

  return (
    <div
      ref={wrapperRef}
      onBlur={handleBlur}
      className={cn(
        'flex items-center focus-within:ring-2',
        className?.includes('ring-danger') ? 'focus-within:ring-danger' : 'focus-within:ring-primary',
        className,
      )}
    >
      <input {...props} {...segmentHandlers('day')} ref={dayRef} size={2} />
      <span className="select-none text-text-muted">/</span>
      <input {...segmentHandlers('month')} ref={monthRef} size={2} tabIndex={-1} />
      <span className="select-none text-text-muted">/</span>
      <input {...segmentHandlers('year')} ref={yearRef} size={4} tabIndex={-1} />
    </div>
  );
}
